import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { MagnifyingGlass, UploadSimple, Spinner, List as ListIcon, SquaresFour, TextAa, FolderSimple, CaretDown, ArrowsDownUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import PdfViewer from "@/components/PdfViewer";
import DmsTree, { matchesView, viewTitle } from "@/components/dms/DmsTree";
import DmsList from "@/components/dms/DmsList";
import DmsPreview from "@/components/dms/DmsPreview";
import DmsBulkBar from "@/components/dms/DmsBulkBar";
import { KAT_LABEL } from "@/components/dms/dmsUtils";

const SORTS = [
  { key: "datum_desc", label: "Neueste zuerst" },
  { key: "datum_asc", label: "Älteste zuerst" },
  { key: "name", label: "Name A–Z" },
  { key: "groesse", label: "Größe" },
  { key: "akte", label: "Akte" },
];

export default function Dokumente() {
  const [docs, setDocs] = useState([]);
  const [akten, setAkten] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState({ view: "alle" });
  const [q, setQ] = useState("");
  const [volltext, setVolltext] = useState(false);
  const [hits, setHits] = useState({});
  const [searching, setSearching] = useState(false);
  const [sort, setSort] = useState("datum_desc");
  const [view, setView] = useState(() => localStorage.getItem("dms_view") || "list");
  const [selected, setSelected] = useState(new Set());
  const [activeId, setActiveId] = useState(null);
  const [fullDoc, setFullDoc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [mobilePreview, setMobilePreview] = useState(false);
  const fileRef = useRef();

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [d, a, t] = await Promise.all([api.get("/dokumente", { params: { alle: true } }), api.get("/akten"), api.get("/dokumente/tags")]);
      setDocs(d.data); setAkten(a.data); setTags(t.data);
    } catch (err) { toast.error(errMsg(err, "Dokumente konnten nicht geladen werden")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { localStorage.setItem("dms_view", view); }, [view]);

  // Volltextsuche (debounced)
  useEffect(() => {
    if (!volltext || q.trim().length < 2) { setHits({}); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try { const r = await api.get("/dokumente/volltext", { params: { q: q.trim() } }); setHits(Object.fromEntries(r.data.map(h => [h.id, h]))); }
      catch { setHits({}); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [q, volltext]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = docs.filter(d => {
      if (!matchesView(d, sel)) return false;
      if (!s) return true;
      if (hits[d.id]) return true;
      const hay = [d.original_filename, d.akte?.aktenzeichen, d.akte?.mandant_name, d.aussteller, d.notiz, ...(d.tags || []), d.source_meta?.subject, d.source_meta?.from].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(s);
    });
    const cmp = {
      datum_desc: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      datum_asc: (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
      name: (a, b) => (a.original_filename || "").localeCompare(b.original_filename || "", "de"),
      groesse: (a, b) => (b.size || 0) - (a.size || 0),
      akte: (a, b) => (a.akte?.aktenzeichen || "zzz").localeCompare(b.akte?.aktenzeichen || "zzz"),
    }[sort];
    return [...list].sort(cmp);
  }, [docs, sel, q, hits, sort]);

  const active = docs.find(d => d.id === activeId) || null;

  const patchLocal = (id, p) => setDocs(ds => ds.map(d => {
    if (d.id !== id) return d;
    const next = { ...d, ...p };
    if ("akte_id" in p) { const a = akten.find(x => x.id === p.akte_id); next.akte = a ? { id: a.id, aktenzeichen: a.aktenzeichen, mandant_name: a.mandant_name, status: a.status, art: a.art } : null; next.akte_geloescht = false; }
    return next;
  }));
  const refreshTags = () => api.get("/dokumente/tags").then(r => setTags(r.data)).catch(() => {});

  const openDoc = (d) => { setActiveId(d.id); if (window.innerWidth < 1024) setMobilePreview(true); };
  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(s => (filtered.every(d => s.has(d.id)) ? new Set() : new Set(filtered.map(d => d.id))));

  const remove = async (d) => {
    if (!window.confirm(`„${d.original_filename}“ löschen?`)) return;
    try { await api.delete(`/dokumente/${d.id}`); toast.success("Dokument gelöscht"); setDocs(ds => ds.filter(x => x.id !== d.id)); if (activeId === d.id) { setActiveId(null); setMobilePreview(false); } }
    catch (err) { toast.error(errMsg(err, "Löschen fehlgeschlagen")); }
  };

  const star = (d) => api.patch(`/dokumente/${d.id}`, { favorit: !d.favorit }).then(() => patchLocal(d.id, { favorit: !d.favorit })).catch(err => toast.error(errMsg(err, "Fehler")));

  const bulk = async (aktion, wert) => {
    const ids = Array.from(selected);
    if (aktion === "loeschen" && !window.confirm(`${ids.length} Dokument${ids.length > 1 ? "e" : ""} löschen?`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/dokumente/bulk", { ids, aktion, wert });
      toast.success(`${r.data.anzahl} Dokument${r.data.anzahl === 1 ? "" : "e"} aktualisiert`);
      if (aktion === "loeschen") { setSelected(new Set()); if (ids.includes(activeId)) setActiveId(null); }
      await load(true);
      if (aktion === "tag") refreshTags();
    } catch (err) { toast.error(errMsg(err, "Sammelaktion fehlgeschlagen")); }
    finally { setBulkBusy(false); }
  };

  const zip = async () => {
    setBulkBusy(true);
    try {
      const r = await api.post("/dokumente/zip", { ids: Array.from(selected) }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `Dokumente_${new Date().toISOString().slice(0, 10)}.zip`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) { toast.error(errMsg(err, "ZIP konnte nicht erstellt werden")); }
    finally { setBulkBusy(false); }
  };

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      if (sel.view === "akte" && sel.akteId) fd.append("akte_id", sel.akteId);
      fd.append("kategorie", sel.kat || (sel.view === "kategorie" ? sel.kat : "sonstige") || "sonstige");
      try { await api.post("/dokumente/upload", fd); ok++; }
      catch (err) { toast.error(errMsg(err, `Upload „${f.name}“ fehlgeschlagen`)); }
    }
    setUploading(false);
    if (ok) toast.success(`${ok} Dokument${ok > 1 ? "e" : ""} hochgeladen${sel.view === "akte" ? " und der Akte zugeordnet" : ""}`);
    load(true);
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); upload(Array.from(e.dataTransfer.files || [])); };

  const preview = active ? (
    <DmsPreview doc={active} akten={akten} allTags={tags}
      onPatch={(id, p) => { patchLocal(id, p); if ("tags" in p) refreshTags(); }}
      onDelete={remove} onClose={() => { setActiveId(null); setMobilePreview(false); }} onFull={() => setFullDoc(active)}
      onAnnotCount={(id, n) => patchLocal(id, { annotationen: n })} onReplace={(d) => setDocs(ds => ds.map(x => x.id === d.id ? { ...x, ...d, akte: x.akte } : x))} />
  ) : (
    <div className="h-full flex flex-col items-center justify-center text-center p-8 text-[#9CA3AF]" data-testid="dms-preview-empty">
      <FolderSimple size={40} weight="duotone" />
      <p className="text-sm mt-3">Dokument auswählen, um Vorschau, Details und Markierungstools zu sehen.</p>
    </div>
  );

  return (
    <div className="flex flex-col lg:h-[calc(100vh-7.5rem)] xl:h-[calc(100vh-8.5rem)] -mb-4" data-testid="dokumente-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <p className="overline text-[#D32F2F] mb-1">Dokumentenmanagement</p>
          <h1 className="font-serif text-3xl sm:text-4xl leading-tight" data-testid="dms-title">{viewTitle(sel, akten)} <span className="text-[#9CA3AF] text-2xl">· {filtered.length}</span></h1>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" multiple accept="application/pdf,image/*" className="hidden" data-testid="dms-file-input" onChange={e => { upload(Array.from(e.target.files || [])); e.target.value = ""; }} />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="dms-upload-btn">
            {uploading ? <Spinner size={16} className="mr-2 animate-spin" /> : <UploadSimple size={16} className="mr-2" />} Hochladen{sel.view === "akte" ? " in Akte" : ""}
          </Button>
        </div>
      </div>

      {/* Mobile: Ordner-Auswahl */}
      <div className="lg:hidden mb-3">
        <details className="bg-white border border-[#E5E7EB] rounded-lg">
          <summary className="list-none px-3 h-10 flex items-center justify-between text-sm cursor-pointer" data-testid="dms-mobile-tree-toggle"><span className="inline-flex items-center gap-2"><FolderSimple size={16} weight="duotone" /> {viewTitle(sel, akten)}</span><CaretDown size={14} /></summary>
          <div className="border-t border-[#E5E7EB] max-h-[50vh] overflow-auto"><DmsTree docs={docs} akten={akten} tags={tags} sel={sel} onSelect={(s) => { setSel(s); setSelected(new Set()); }} testId="dms-tree-mobile" /></div>
        </details>
      </div>

      <div className="flex-1 min-h-0 bg-white border border-[#E5E7EB] rounded-lg overflow-hidden flex flex-col">
        <div className="border-b border-[#E5E7EB] px-3 py-2 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder={volltext ? "Im Inhalt der Dokumente suchen …" : "Dateiname, Akte, Mandant, Schlagwort …"} className="pl-8 h-9" data-testid="dms-search" />
            {searching && <Spinner size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin text-[#9CA3AF]" />}
          </div>
          <button type="button" onClick={() => setVolltext(v => !v)} data-testid="dms-volltext-toggle" aria-pressed={volltext} title="Auch im Inhalt der Dokumente (Volltext) suchen"
            className={`h-9 px-2.5 rounded-md border text-xs inline-flex items-center gap-1.5 transition-colors ${volltext ? "bg-[#1A2238] text-white border-[#1A2238]" : "border-[#E5E7EB] text-[#4B5563] hover:bg-[#F9FAFB]"}`}>
            <TextAa size={15} /> Volltext
          </button>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-9 w-[160px] text-xs" data-testid="dms-sort"><span className="inline-flex items-center gap-1.5"><ArrowsDownUp size={14} /><SelectValue /></span></SelectTrigger>
            <SelectContent>{SORTS.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <div className="inline-flex rounded-md border border-[#E5E7EB] overflow-hidden" data-testid="dms-view-toggle">
            <button type="button" onClick={() => setView("list")} className={`h-9 w-9 flex items-center justify-center ${view === "list" ? "bg-[#1A2238] text-white" : "text-[#4B5563] hover:bg-[#F9FAFB]"}`} title="Listenansicht" data-testid="dms-view-list"><ListIcon size={16} /></button>
            <button type="button" onClick={() => setView("grid")} className={`h-9 w-9 flex items-center justify-center ${view === "grid" ? "bg-[#1A2238] text-white" : "text-[#4B5563] hover:bg-[#F9FAFB]"}`} title="Kachelansicht" data-testid="dms-view-grid"><SquaresFour size={16} /></button>
          </div>
        </div>

        <div className="hidden lg:flex flex-1 min-h-0">
        <ResizablePanelGroup direction="horizontal" className="flex-1 min-h-0">
          <ResizablePanel defaultSize={18} minSize={12} collapsible className="border-r border-[#E5E7EB] bg-[#FAFAFA]">
            <DmsTree docs={docs} akten={akten} tags={tags} sel={sel} onSelect={(s) => { setSel(s); setSelected(new Set()); }} />
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={active ? 40 : 82} minSize={25}>
            <div className={`h-full overflow-auto relative ${dragOver ? "ring-2 ring-inset ring-[#1A2238]" : ""}`} onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop} data-testid="dms-list-col">
              {selected.size > 0 && <DmsBulkBar count={selected.size} akten={akten} onAction={bulk} onZip={zip} onClear={() => setSelected(new Set())} busy={bulkBusy} />}
              {loading ? <div className="p-12 text-center text-sm text-[#6B7280]">Lädt …</div>
                : <DmsList docs={filtered} view={view} selected={selected} onToggle={toggle} onToggleAll={toggleAll} activeId={activeId} onOpen={openDoc} hits={hits} onStar={star} />}
              {dragOver && <div className="absolute inset-0 bg-[#1A2238]/5 flex items-center justify-center pointer-events-none"><p className="bg-white rounded-lg px-4 py-2 text-sm shadow">Dateien hier ablegen zum Hochladen</p></div>}
            </div>
          </ResizablePanel>
          {active && (
            <>
              <ResizableHandle withHandle />
              <ResizablePanel defaultSize={42} minSize={28}>{preview}</ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
        </div>

        {/* Mobile / Tablet: nur Liste, Vorschau als Sheet */}
        <div className="lg:hidden flex-1 min-h-0 overflow-auto relative" onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop}>
          {selected.size > 0 && <DmsBulkBar count={selected.size} akten={akten} onAction={bulk} onZip={zip} onClear={() => setSelected(new Set())} busy={bulkBusy} />}
          {loading ? <div className="p-12 text-center text-sm text-[#6B7280]">Lädt …</div>
            : <DmsList docs={filtered} view={view} selected={selected} onToggle={toggle} onToggleAll={toggleAll} activeId={activeId} onOpen={openDoc} hits={hits} onStar={star} />}
        </div>
      </div>

      <Sheet open={mobilePreview && !!active} onOpenChange={(o) => { if (!o) setMobilePreview(false); }}>
        <SheetContent side="bottom" className="h-[94vh] p-0 lg:hidden [&>button]:hidden" aria-describedby={undefined} data-testid="dms-mobile-preview">
          <SheetTitle className="sr-only">{active?.original_filename || "Dokument"}</SheetTitle>
          {preview}
        </SheetContent>
      </Sheet>

      <PdfViewer open={!!fullDoc} onClose={() => { setFullDoc(null); load(true); }} dokumentId={fullDoc?.id} akteId={fullDoc?.akte_id} dokumentName={fullDoc?.original_filename} contentType={fullDoc?.content_type} />
    </div>
  );
}

export { KAT_LABEL };
